import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  FaFileAlt, 
  FaDownload, 
  FaCalendarAlt, 
  FaFilter,
  FaChartBar,
  FaUsers,
  FaUser,
  FaClipboardCheck,
  FaPrint,
  FaEye,
  FaCheckCircle,
  FaSpinner,
  FaFilePdf,
  FaStar,
  FaTrendingUp,
  FaTrendingDown,
  FaMinus
} from 'react-icons/fa';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useReactToPrint } from 'react-to-print';

const Reports = () => {
  const { user, token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [analyticsData, setAnalyticsData] = useState(null);
  const [studentsData, setStudentsData] = useState([]);
  const [reviewsData, setReviewsData] = useState([]);
  const reportRef = useRef();

  // Load reports from localStorage on component mount
  useEffect(() => {
    const savedReports = localStorage.getItem(`reports_${user?.id}`);
    if (savedReports) {
      setReports(JSON.parse(savedReports));
    }
  }, [user?.id]);

  // Save reports to localStorage whenever reports change
  useEffect(() => {
    if (reports.length > 0) {
      localStorage.setItem(`reports_${user?.id}`, JSON.stringify(reports));
    }
  }, [reports, user?.id]);

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Fetch analytics data
      const analyticsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/analytics/supervisor?timeRange=month`, config);
      setAnalyticsData(analyticsResponse.data);
      
      // Fetch students data
      const studentsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/students/university/${user.university}`, config);
      setStudentsData(studentsResponse.data?.students || []);
      
      // Fetch all reviews data
      const reviewsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/reviews/supervisor`, config);
      setReviewsData(reviewsResponse.data || []);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (reportType) => {
    try {
      setGenerating(true);
      const reportData = await generateReportData(reportType);
      
      const newReport = {
        id: Date.now().toString(),
        type: reportType,
        name: getReportName(reportType),
        data: reportData,
        createdAt: new Date().toISOString(),
        startDate: dateRange.start,
        endDate: dateRange.end
      };
      
      setReports(prev => [newReport, ...prev]);
      setSuccessMessage(`${getReportName(reportType)} generated successfully!`);
      setShowSuccessModal(true);
      
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(`Failed to generate ${getReportName(reportType)} report`);
    } finally {
      setGenerating(false);
    }
  };

  const generateReportData = async (reportType) => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    
    switch (reportType) {
      case 'review-summary':
        return {
          totalReviews: analyticsData?.totalReviews || 0,
          statusDistribution: analyticsData?.statusDistribution || {},
          averageRating: analyticsData?.averageRating || 0,
          weeklyData: analyticsData?.weeklyData || [],
          reviews: reviewsData.filter(review => {
            const reviewDate = new Date(review.submittedDate || review.createdAt);
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            return reviewDate >= startDate && reviewDate <= endDate;
          })
        };
      
      case 'student-performance':
        return {
          students: studentsData.map(student => ({
            ...student,
            performance: calculateStudentPerformance(student)
          })),
          summary: {
            totalStudents: studentsData.length,
            averageScore: calculateAverageScore(),
            topPerformers: getTopPerformers()
          }
        };
      
      case 'analytics':
        return {
          ...analyticsData,
          dateRange,
          generatedAt: new Date().toISOString()
        };
      
      case 'personal-performance':
        const personalResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/analytics/personal-performance?startDate=${dateRange.start}&endDate=${dateRange.end}`, config);
        return personalResponse.data;
      
      default:
        return {};
    }
  };

  const calculateStudentPerformance = (student) => {
    const studentReviews = reviewsData.filter(review => 
      review.studentId === student.studentId && review.status !== 'pending'
    );
    
    if (studentReviews.length === 0) {
      return {
        totalReviews: 0,
        averageScore: 0,
        trend: 'stable',
        completedReviews: 0,
        acceptedReviews: 0,
        deniedReviews: 0
      };
    }
    
    const completedReviews = studentReviews.filter(r => r.status !== 'pending');
    const avgScore = completedReviews.length > 0 
      ? completedReviews.reduce((sum, r) => sum + ((r.procedureQuality || 0) + (r.patientInteraction || 0)) / 2, 0) / completedReviews.length 
      : 0;
    
    return {
      totalReviews: studentReviews.length,
      averageScore: avgScore,
      trend: 'stable', // Simplified for now
      completedReviews: completedReviews.length,
      acceptedReviews: completedReviews.filter(r => r.status === 'accepted').length,
      deniedReviews: completedReviews.filter(r => r.status === 'denied').length
    };
  };

  const calculateAverageScore = () => {
    if (!analyticsData) return 0;
    return analyticsData.averageRating || 0;
  };

  const getTopPerformers = () => {
    return studentsData
      .map(student => ({
        ...student,
        performance: calculateStudentPerformance(student)
      }))
      .filter(s => s.performance.totalReviews > 0)
      .sort((a, b) => b.performance.averageScore - a.performance.averageScore)
      .slice(0, 5);
  };


  const getReportName = (reportType) => {
    const names = {
      'review-summary': 'Review Summary Report',
      'student-performance': 'Student Performance Report',
      'analytics': 'Analytics Report',
      'personal-performance': 'Personal Performance Report'
    };
    return names[reportType] || 'Report';
  };

  const downloadPDF = async (report) => {
    try {
      // If report is in modal, use the modal content
      let element;
      if (showReportModal && selectedReport?.id === report.id) {
        element = document.getElementById(`report-${report.id}`);
      } else {
        // Generate a temporary element for the report
        const tempElement = document.createElement('div');
        tempElement.innerHTML = `
          <div id="temp-report-${report.id}" class="bg-white p-8 max-w-4xl mx-auto">
            ${generateReportHTML(report)}
          </div>
        `;
        document.body.appendChild(tempElement);
        element = document.getElementById(`temp-report-${report.id}`);
      }
      
      if (!element) return;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`${report.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      
      // Clean up temporary element
      const tempElement = document.getElementById(`temp-report-${report.id}`);
      if (tempElement) {
        tempElement.remove();
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError('Failed to generate PDF');
    }
  };

  const generateReportHTML = (report) => {
    // This will be used to generate HTML for reports not currently in modal
    return `<div>Report content for ${report.name}</div>`;
  };

  const handlePrint = useReactToPrint({
    content: () => reportRef.current,
    documentTitle: selectedReport?.name || 'Report'
  });

  const reportTypes = [
    {
      id: 'review-summary',
      name: 'Review Summary',
      description: 'Summary of all reviews in the selected period',
      icon: <FaClipboardCheck className="h-6 w-6" />,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      id: 'student-performance',
      name: 'Student Performance',
      description: 'Performance analysis of students',
      icon: <FaUsers className="h-6 w-6" />,
      color: 'bg-green-100 text-green-600'
    },
    {
      id: 'analytics',
      name: 'Analytics Report',
      description: 'Detailed analytics and insights',
      icon: <FaChartBar className="h-6 w-6" />,
      color: 'bg-purple-100 text-purple-600'
    },
    {
      id: 'personal-performance',
      name: 'Personal Performance',
      description: 'Your personal review performance and analytics',
      icon: <FaUser className="h-6 w-6" />,
      color: 'bg-orange-100 text-orange-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#0077B6]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reports</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Generate and manage comprehensive reports</p>
          </div>
          <div className="text-xs sm:text-sm text-gray-500">
            {reports.length} reports available
          </div>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Select Date Range</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm w-full"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6] text-sm w-full"
            />
          </div>
        </div>
      </div>

      {/* Generate Reports */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate New Report</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reportTypes.map((reportType) => (
            <motion.div
              key={reportType.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => generateReport(reportType.id)}
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className={`p-3 rounded-lg ${reportType.color}`}>
                  {reportType.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">{reportType.name}</h4>
                  <p className="text-sm text-gray-600">{reportType.description}</p>
                </div>
              </div>
              <button 
                className="w-full px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors flex items-center justify-center"
                disabled={generating}
              >
                {generating ? (
                  <>
                    <FaSpinner className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-xl shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Generated Reports</h3>
        </div>
        <div className="divide-y divide-gray-200">
          {reports.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FaFileAlt className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p>No reports generated yet</p>
            </div>
          ) : (
            reports.map((report, index) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 bg-[rgba(0,119,182,0.1)] rounded-lg">
                      <FaFilePdf className="h-6 w-6 text-[#0077B6]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{report.name}</h4>
                      <p className="text-sm text-gray-600">
                        Generated on {new Date(report.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-gray-500">
                        Period: {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => downloadPDF(report)}
                      className="p-2 text-gray-400 hover:text-[#0077B6] transition-colors"
                      title="Download PDF"
                    >
                      <FaDownload className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedReport(report);
                        setShowReportModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-[#0077B6] transition-colors"
                      title="View"
                    >
                      <FaEye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Report Detail Modal */}
      {showReportModal && selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-0"
          >
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">{selectedReport.name}</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => downloadPDF(selectedReport)}
                    className="flex items-center px-4 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-[#005a8b] transition-colors"
                  >
                    <FaDownload className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                  <button
                    onClick={handlePrint}
                    className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <FaPrint className="h-4 w-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => setShowReportModal(false)}
                    className="text-gray-400 hover:text-gray-600 ml-4"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6" ref={reportRef}>
              <div id={`report-${selectedReport.id}`} className="bg-white">
                <ReportContent report={selectedReport} />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl p-4 sm:p-6 max-w-sm sm:max-w-md w-full mx-2 sm:mx-4"
          >
            <div className="text-center">
              <FaCheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Success!</h3>
              <p className="text-gray-600">{successMessage}</p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// Report Content Component
const ReportContent = ({ report }) => {
  const { user } = useAuth();
  
  const renderReviewSummary = (data) => (
    <div className="space-y-6">
      <div className="text-center border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Review Summary Report</h1>
        <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
        <p className="text-sm text-gray-500">
          Period: {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-blue-600">{data.totalReviews}</h3>
          <p className="text-blue-800">Total Reviews</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-green-600">{data.statusDistribution.accepted || 0}</h3>
          <p className="text-green-800">Accepted</p>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-yellow-600">{data.averageRating?.toFixed(1) || '0.0'}</h3>
          <p className="text-yellow-800">Average Rating</p>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Review Trends</h3>
        <div className="grid grid-cols-7 gap-2">
          {data.weeklyData?.map((count, index) => (
            <div key={index} className="text-center">
              <div className="bg-blue-100 h-16 rounded flex items-end justify-center">
                <div 
                  className="bg-blue-500 w-full rounded-t"
                  style={{ height: `${Math.max((count / Math.max(...data.weeklyData)) * 100, 5)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600 mt-1">Day {index + 1}</p>
              <p className="text-xs font-semibold">{count}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStudentPerformance = (data) => (
    <div className="space-y-6">
      <div className="text-center border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Student Performance Report</h1>
        <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
        <p className="text-sm text-gray-500">
          Period: {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-blue-600">{data.summary.totalStudents}</h3>
          <p className="text-blue-800">Total Students</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-green-600">{data.summary.averageScore?.toFixed(1) || '0.0'}</h3>
          <p className="text-green-800">Average Score</p>
        </div>
        <div className="bg-purple-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-purple-600">{data.summary.topPerformers?.length || 0}</h3>
          <p className="text-purple-800">Top Performers</p>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Performance Details</h3>
        <div className="space-y-4">
          {data.students?.slice(0, 10).map((student, index) => (
            <div key={student.id} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">{student.name}</h4>
                  <p className="text-sm text-gray-600">ID: {student.studentId}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <span className="text-lg font-bold text-gray-900">
                      {student.performance?.averageScore?.toFixed(1) || '0.0'}
                    </span>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <FaStar
                          key={star}
                          className={`h-4 w-4 ${
                            star <= (student.performance?.averageScore || 0) ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    {student.performance?.totalReviews || 0} reviews
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalytics = (data) => (
    <div className="space-y-6">
      <div className="text-center border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Report</h1>
        <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
        <p className="text-sm text-gray-500">
          Period: {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-blue-600">{data.totalReviews || 0}</h3>
          <p className="text-blue-800">Total Reviews</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-green-600">{data.statusDistribution?.accepted || 0}</h3>
          <p className="text-green-800">Accepted</p>
        </div>
        <div className="bg-red-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-red-600">{data.statusDistribution?.denied || 0}</h3>
          <p className="text-red-800">Denied</p>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-yellow-600">{data.averageRating?.toFixed(1) || '0.0'}</h3>
          <p className="text-yellow-800">Avg Rating</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Weekly Trends</h3>
          <div className="space-y-2">
            {data.weeklyData?.map((count, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Day {index + 1}</span>
                <div className="flex items-center space-x-2">
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${Math.max((count / Math.max(...(data.weeklyData || [1]))) * 100, 5)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold w-8">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Students</h3>
          <div className="space-y-2">
            {data.topStudents?.slice(0, 5).map((student, index) => (
              <div key={student.id} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{student.name}</span>
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-semibold">{student.averageRating?.toFixed(1) || '0.0'}</span>
                  <FaStar className="h-3 w-3 text-yellow-400" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Metrics</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Procedure Quality</span>
              <span className="text-sm font-semibold">{data.averageRatings?.procedureQuality?.toFixed(1) || '0.0'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Patient Interaction</span>
              <span className="text-sm font-semibold">{data.averageRatings?.patientInteraction?.toFixed(1) || '0.0'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Overall Average</span>
              <span className="text-sm font-semibold">{data.averageRatings?.overall?.toFixed(1) || '0.0'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Performance Trends</h3>
        <div className="grid grid-cols-6 gap-4">
          {data.monthlyLabels?.slice(-6).map((month, index) => (
            <div key={index} className="text-center">
              <div className="bg-blue-100 h-20 rounded flex items-end justify-center mb-2">
                <div 
                  className="bg-blue-500 w-full rounded-t"
                  style={{ height: `${Math.max((data.averageRatings?.[index] || 0) / 5 * 100, 5)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-600">{month}</p>
              <p className="text-xs font-semibold">{data.averageRatings?.[index]?.toFixed(1) || '0.0'}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPersonalPerformance = (data) => (
    <div className="space-y-6">
      <div className="text-center border-b pb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Personal Performance Report</h1>
        <p className="text-gray-600">Generated on {new Date().toLocaleDateString()}</p>
        <p className="text-sm text-gray-500">
          Period: {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
        </p>
        <p className="text-sm text-blue-600 font-medium">Supervisor: {data.supervisor?.name}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-blue-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-blue-600">{data.summary.totalReviews}</h3>
          <p className="text-blue-800">Total Reviews</p>
        </div>
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-green-600">{data.summary.acceptedReviews}</h3>
          <p className="text-green-800">Accepted</p>
        </div>
        <div className="bg-red-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-red-600">{data.summary.deniedReviews}</h3>
          <p className="text-red-800">Denied</p>
        </div>
        <div className="bg-yellow-50 p-6 rounded-lg text-center">
          <h3 className="text-2xl font-bold text-yellow-600">{data.summary.overallAverageRating?.toFixed(1) || '0.0'}</h3>
          <p className="text-yellow-800">Avg Rating</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Breakdown</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Procedure Quality</span>
                <span className="text-sm font-semibold">{data.summary.averageProcedureQuality?.toFixed(1) || '0.0'}/5.0</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-500 h-2 rounded-full"
                  style={{ width: `${(data.summary.averageProcedureQuality || 0) / 5 * 100}%` }}
                ></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Patient Interaction</span>
                <span className="text-sm font-semibold">{data.summary.averagePatientInteraction?.toFixed(1) || '0.0'}/5.0</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${(data.summary.averagePatientInteraction || 0) / 5 * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Trends (Last 7 Days)</h3>
          <div className="h-48 flex items-end justify-between space-x-1">
            {data.summary.reviewTrends?.map((trend, index) => {
              const maxCount = Math.max(...data.summary.reviewTrends.map(t => t.count), 1);
              const height = (trend.count / maxCount) * 100;
              return (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className="w-full bg-orange-100 rounded-t flex items-end justify-center" style={{ height: '120px' }}>
                    <div 
                      className="w-full bg-orange-500 rounded-t transition-all duration-300 hover:bg-orange-600"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${trend.date}: ${trend.count} reviews`}
                    ></div>
                  </div>
                  <div className="text-xs text-gray-600 mt-2 font-medium">{trend.date}</div>
                  <div className="text-xs font-semibold text-gray-800">{trend.count}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Performance Trends</h3>
        <div className="h-64 flex items-end justify-between space-x-2">
          {data.monthlyTrends?.map((month, index) => {
            const maxCount = Math.max(...data.monthlyTrends.map(m => m.count), 1);
            const height = (month.count / maxCount) * 100;
            return (
              <div key={index} className="flex flex-col items-center flex-1">
                <div className="w-full bg-blue-100 rounded-t flex items-end justify-center" style={{ height: '180px' }}>
                  <div 
                    className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                    style={{ height: `${Math.max(height, 5)}%` }}
                    title={`${month.month}: ${month.count} reviews`}
                  ></div>
                </div>
                <div className="text-xs text-gray-600 mt-2 font-medium">{month.month}</div>
                <div className="text-xs font-semibold text-gray-800">{month.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Trends Over Time</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Procedure Quality Trend</h4>
            <div className="h-32 flex items-end justify-between space-x-1">
              {data.qualityTrends?.map((trend, index) => {
                const maxQuality = 5; // Maximum quality rating
                const height = (trend.procedureQuality / maxQuality) * 100;
                return (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div className="w-full bg-green-100 rounded-t flex items-end justify-center" style={{ height: '80px' }}>
                      <div 
                        className="w-full bg-green-500 rounded-t transition-all duration-300 hover:bg-green-600"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${trend.period}: ${trend.procedureQuality?.toFixed(1) || '0.0'}/5.0`}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 font-medium">{trend.period}</div>
                    <div className="text-xs font-semibold text-gray-800">{trend.procedureQuality?.toFixed(1) || '0.0'}</div>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Patient Interaction Trend</h4>
            <div className="h-32 flex items-end justify-between space-x-1">
              {data.qualityTrends?.map((trend, index) => {
                const maxInteraction = 5; // Maximum interaction rating
                const height = (trend.patientInteraction / maxInteraction) * 100;
                return (
                  <div key={index} className="flex flex-col items-center flex-1">
                    <div className="w-full bg-purple-100 rounded-t flex items-end justify-center" style={{ height: '80px' }}>
                      <div 
                        className="w-full bg-purple-500 rounded-t transition-all duration-300 hover:bg-purple-600"
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${trend.period}: ${trend.patientInteraction?.toFixed(1) || '0.0'}/5.0`}
                      ></div>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 font-medium">{trend.period}</div>
                    <div className="text-xs font-semibold text-gray-800">{trend.patientInteraction?.toFixed(1) || '0.0'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Reviews</h3>
        <div className="space-y-3">
          {data.reviews?.slice(0, 5).map((review, index) => (
            <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-900">Student: {review.studentName}</h4>
                  <p className="text-sm text-gray-600">Procedure: {review.procedureType}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">Quality: {review.procedureQuality || 'N/A'}</span>
                    <span className="text-sm text-gray-600">Interaction: {review.patientInteraction || 'N/A'}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(review.submittedDate || review.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderReportContent = () => {
    switch (report.type) {
      case 'review-summary':
        return renderReviewSummary(report.data);
      case 'student-performance':
        return renderStudentPerformance(report.data);
      case 'analytics':
        return renderAnalytics(report.data);
      case 'personal-performance':
        return renderPersonalPerformance(report.data);
      default:
        return <div>Report content not available</div>;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {renderReportContent()}
    </div>
  );
};

export default Reports;